import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Article } from '@/types/article';
import { QuestionCatalog } from '@/services/questionCatalogs';
import { useAuth } from '@/hooks/useClerkAuth';
import { fetchArticles } from '@/services/articles';
import { fetchQuestionCatalogs } from '@/services/questionCatalogs';
import TopNavBar from '@/components/TopNavBar';
import SignUpBenefits from '@/components/SignUpBenefits';
import SEO from '@/hooks/useSEO';
import Articles from './Articles';
import Admin from './Admin';

const Index = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState<Article[]>([]);
  const [catalogs, setCatalogs] = useState<QuestionCatalog[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const { user, isLoading, canCreateArticle } = useAuth();
  const navigate = useNavigate();

  const currentView =
    searchParams.get('view') === 'admin' ? 'admin' : 'articles';

  // Load articles from Supabase - wait for auth to be ready first
  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoadingArticles(true);
        const fetchedArticles = await fetchArticles();
        setArticles(fetchedArticles);
      } catch (error) {
        console.error('Error loading articles:', error);
        // Don't show error to user, just log it
      } finally {
        setLoadingArticles(false);
      }
    };

    // Only load articles after auth has finished loading
    if (!isLoading) {
      loadArticles();
    }
  }, [isLoading]);

  // Load question catalogs from Supabase
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        setLoadingCatalogs(true);
        const fetchedCatalogs = await fetchQuestionCatalogs();
        setCatalogs(fetchedCatalogs);
      } catch (error) {
        console.error('Error loading question catalogs:', error);
      } finally {
        setLoadingCatalogs(false);
      }
    };

    if (!isLoading) {
      loadCatalogs();
    }
  }, [isLoading]);

  const handleArticleAdded = (newArticle: Article) => {
    setArticles(prev => [newArticle, ...prev]);
    setSearchParams({});
  };

  const navigateToAdmin = () => {
    if (!user || !canCreateArticle) {
      navigate('/sign-in');
      return;
    }
    setSearchParams({ view: 'admin' });
  };

  const navigateToArticles = () => {
    setSearchParams({});
  };

  if (isLoading || loadingArticles || loadingCatalogs) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show sign-up benefits page if trying to access admin without authentication or proper role
  if (currentView === 'admin' && (!user || !canCreateArticle)) {
    return (
      <SignUpBenefits
        onSignUp={() => navigate('/sign-up')}
        onSignIn={() => navigate('/sign-in')}
      />
    );
  }

  // Show admin page if user is authenticated and has proper role
  if (currentView === 'admin' && user && canCreateArticle()) {
    return (
      <>
        <TopNavBar
          currentPage="admin"
          onNavigateToAdmin={navigateToAdmin}
          onNavigateToArticles={navigateToArticles}
          onNavigateToDashboard={() => navigate('/dashboard')}
          onNavigateToQuestions={() => navigate('/questions')}
        />
        <Admin onArticleAdded={handleArticleAdded} />
      </>
    );
  }

  // Show articles page (public access)
  return (
    <>
      <SEO
        title="German Reading Practice with Translations"
        description="Read German articles with instant English and Vietnamese translations. Improve your German with sentence-by-sentence breakdowns and difficult-verb explanations."
        canonicalPath="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Lingua Craft',
          url: 'https://linguacraft.de',
          description:
            'German language learning through immersive reading with translations.',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://linguacraft.de/?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <TopNavBar
        currentPage="articles"
        onNavigateToAdmin={navigateToAdmin}
        onNavigateToArticles={navigateToArticles}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <Articles
        articles={articles}
        catalogs={catalogs}
        onNavigateToAdmin={navigateToAdmin}
        showAdminButton={canCreateArticle()}
        requireAuthForReading={false}
      />
    </>
  );
};

export default Index;
